import React, { useState, useEffect } from 'react';
import { useAccount } from 'wagmi';
import { apiUrl } from '../utils/constants.ts'; // Assuming apiUrl is defined here
import ClipLoader from 'react-spinners/ClipLoader';

interface UsernameModalProps {
    isOpen: boolean;
    onClose: () => void;
    currentUsername: string;
    walletAddress: string | undefined;
    onUsernameUpdate: (newUsername: string) => void; // Callback to update parent state
}

const UsernameModal: React.FC<UsernameModalProps> = ({
    isOpen,
    onClose,
    currentUsername,
    walletAddress,
    onUsernameUpdate,
}) => {
    const [newUsername, setNewUsername] = useState(currentUsername);
    const [isLoading, setIsLoading] = useState(false);
    const [error, setError] = useState<string | null>(null);
    const { address } = useAccount(); // Get connected address for verification

    useEffect(() => {
        // Reset state when modal opens or closes
        if (isOpen) {
            // Optionally pre-fill with current username if it's not default
            if (currentUsername && !currentUsername.startsWith('0x') && !currentUsername.endsWith('...')) {
                 setNewUsername(currentUsername);
            } else {
                 setNewUsername(''); // Clear if it's default or empty
            }
            setError(null);
            setIsLoading(false);
        } else {
            setNewUsername('');
            setError(null);
            setIsLoading(false);
        }
    }, [isOpen, currentUsername]);

    const handleSave = async () => {
        if (!walletAddress || !address || walletAddress.toLowerCase() !== address.toLowerCase()) {
            setError("Wallet address mismatch or not connected.");
            return;
        }
        const trimmedUsername = newUsername.trim(); // Trim username first
        if (!trimmedUsername) { // Check trimmed value
            setError("Username cannot be empty.");
            return;
        }
        if (trimmedUsername.length < 3 || trimmedUsername.length > 20) { // Check length of trimmed value
            setError("Username must be 3-20 characters long.");
            return;
        }
         // --- FIX: Allow spaces in the regex ---
         if (!/^[a-zA-Z0-9_ ]+$/.test(trimmedUsername)) { // Added space to the character set
            setError("Username can only contain letters, numbers, underscores, and spaces."); // Updated error message
            return;
        }
        // --- END FIX ---


        setIsLoading(true);
        setError(null);

        try {
            // --- ADD CONSOLE LOG ---
            const requestBody = {
                walletAddress: address, // Send connected address for verification
                username: trimmedUsername, // Use the already trimmed value
            };
            console.log("Sending request body:", JSON.stringify(requestBody));
            // --- END CONSOLE LOG ---

            const response = await fetch(`${apiUrl}/api/profile/username`, {
                method: 'POST',
                headers: {
                    'Content-Type': 'application/json',
                },
                body: JSON.stringify(requestBody), // Use the logged variable
            });

            const result = await response.json();

            if (!response.ok) {
                // Log the error response from backend for more details
                console.error("Backend error response:", result);
                throw new Error(result.error || `HTTP error! status: ${response.status}`);
            }

            if (result.success) {
                onUsernameUpdate(trimmedUsername); // Update parent state with the trimmed username
                onClose(); // Close modal on success
            } else {
                setError(result.error || "An unknown error occurred.");
            }
        } catch (err: any) {
            console.error("Failed to update username:", err);
            setError(err.message || "Failed to update username. Please try again.");
        } finally {
            setIsLoading(false);
        }
    };

    if (!isOpen) {
        return null;
    }

    return (
        <div className="fixed inset-0 bg-black bg-opacity-70 flex justify-center items-center z-50 backdrop-blur-sm">
            <div className="bg-gradient-to-br from-[#2c3e50] to-[#34495e] p-6 rounded-lg shadow-xl w-full max-w-md border border-gray-500 relative">
                <button
                    onClick={onClose}
                    className="absolute top-2 right-3 text-gray-300 hover:text-white text-2xl font-bold"
                    disabled={isLoading}
                >
                    &times;
                </button>
                <h2 className="text-2xl font-bold mb-4 text-white text-center">Set Username</h2>

                <div className="mb-4">
                    {/* --- FIX: Update label text --- */}
                    <label htmlFor="username" className="block text-sm font-medium text-gray-300 mb-1">
                        New Username (3-20 chars, A-Z, 0-9, _, space)
                    </label>
                    {/* --- END FIX --- */}
                    <input
                        type="text"
                        id="username"
                        value={newUsername}
                        onChange={(e) => setNewUsername(e.target.value)}
                        className="w-full px-3 py-2 bg-gray-700 border border-gray-600 rounded-md text-white focus:outline-none focus:ring-2 focus:ring-[#7db4e5] focus:border-transparent placeholder-gray-400"
                        placeholder="Enter your desired username"
                        maxLength={20}
                        disabled={isLoading}
                    />
                </div>

                {error && (
                    <p className="text-red-400 text-sm mb-3 text-center">{error}</p>
                )}

                <div className="flex justify-center">
                    <button
                        onClick={handleSave}
                        className="bg-[#7db4e5] hover:bg-[#6ca3d1] text-white font-bold py-2 px-6 rounded-lg focus:outline-none focus:shadow-outline transition duration-150 ease-in-out disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center min-w-[100px]"
                        disabled={isLoading || !newUsername.trim()}
                    >
                        {isLoading ? (
                            <ClipLoader color={'#ffffff'} loading={true} size={20} />
                        ) : (
                            'Save'
                        )}
                    </button>
                </div>

                 {/* Optional: Display username history - Fetch this separately if needed */}
                 {/* <div className="mt-4">
                     <h3 className="text-lg font-semibold text-gray-300 mb-2">Previous Usernames:</h3>
                     <ul className="list-disc list-inside text-gray-400 text-sm">
                         {usernameHistory.map((item, index) => (
                             <li key={index}>{item.username} (on {new Date(item.timestamp).toLocaleDateString()})</li>
                         ))}
                         {usernameHistory.length === 0 && <li>No previous usernames</li>
                         )}
                     </ul>
                 </div> */}
            </div>
        </div>
    );
};

export default UsernameModal;
